/**
 * <bts-add-on-manager>
 * BTS 加购区块：主产品变体选择（variant-picker-202608）、配件色块、汇总价格、加购入车。
 * 变体交互参考 interactive-product-card-gen3-ja。
 */
class BtsAddOnManager extends HTMLElement {
  #controller = null;
  #ready = false;
  #addonChecked = true;

  connectedCallback() {
    if (this.#ready) return;
    this.#ready = true;
    this.#controller = new AbortController();

    this.getFormattedMoney = window.getFormattedMoney;
    this.addonPromoCents = Number(this.dataset.addonPromo || 0);
    this.discountCode = (this.dataset.discountCode || "").trim();
    this.currencyCodeEnabled = this.dataset.currencyCode === "true";
    this.soldOutText = this.dataset.soldOutText || "Sold Out";
    this.ctaDefaultText = this.querySelector("[data-bts-atc-text]")?.textContent || "";

    this.mainCard = this.querySelector("[data-bts-main]");
    this.addonCard = this.querySelector("[data-bts-addon]");
    this.mainPriceEl = this.querySelector("[data-bts-main-price]");
    this.addonPriceEl = this.querySelector("[data-bts-addon-price]");
    this.addonCompareEl = this.querySelector("[data-bts-addon-compare]");
    this.sumMainEl = this.querySelector("[data-bts-sum-main]");
    this.sumAddonRow = this.querySelector("[data-bts-sum-addon-row]");
    this.sumAddonPriceEl = this.querySelector("[data-bts-sum-addon-price]");
    this.sumAddonCompareEl = this.querySelector("[data-bts-sum-addon-compare]");
    this.sumSaveRow = this.querySelector("[data-bts-sum-save-row]");
    this.sumSaveEl = this.querySelector("[data-bts-sum-save]");
    this.sumTotalEl = this.querySelector("[data-bts-sum-total]");
    this.atcBtn = this.querySelector("[data-bts-atc]");
    this.atcTextEl = this.querySelector("[data-bts-atc-text]");
    this.addonToggle = this.querySelector("[data-bts-addon-toggle]");
    this.form = this.querySelector("form[is='product-form']") || this.querySelector("form.bts-add-on__form");
    this.addonBridge = this.querySelector("[data-bts-addon-bridge]");
    this.discountInput = this.querySelector("[data-bts-discount-code]");

    this.main = this.#createMainPicker(this.mainCard);
    this.addon = this.#createAddonPicker(this.addonCard);

    this.#bindGlobalEvents();
    this.main?.init();
    this.addon?.init();
    this.#updateSummary();
    this.#updateAtcState();
    this.#syncAddonBridge();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#ready = false;
  }

  /* ----------------------------- Main picker ----------------------------- */

  #createMainPicker(card) {
    if (!card) return null;

    const root = card;
    const picker = {
      root,
      media: root.querySelector("[data-bts-main-media] img"),
      variantIdInput: root.querySelector("[data-variant-id-input]") || root.querySelector("[data-bts-main-variant-id]"),
      colorSwatches: root.querySelectorAll(".button-color-swatch"),
      sizeOptionWrap: root.querySelector(".simple-variant-option.size-option"),
      colorPosition: root.querySelector(".simple-variant-option.color-option")?.dataset.optionPosition || "1",
      sizePosition: root.querySelector(".simple-variant-option.size-option")?.dataset.sizePosition || "2",
      unknownSizeValue: root.querySelector(".simple-variant-option.size-option")?.dataset.unknownSize || "",
      sizeUnknownBtn: root.querySelector(".size-btn-unknown"),
      sizeKnowBtn: root.querySelector(".size-btn-know-trigger"),
      sizeKnowOverlayBtn: root.querySelector(".size-btn-know-overlay"),
      sizeDropdown: root.querySelector(".size-dropdown"),
      sizeDropdownOverlay: root.querySelector(".size-dropdown-overlay"),
      sizeOptions: root.querySelector(".size-options"),
      sizeOptionBtns: root.querySelectorAll(".size-option-btn"),
      sizeBtnText: root.querySelector(".size-btn-text"),
      sizeBtnTextOverlay: root.querySelector(".size-btn-text-overlay"),
      variants: this.#loadVariantData(root),
      currentVariant: null,
      selectedOptions: {},
    };

    picker.init = () => {
      if (!picker.variants.length && picker.variantIdInput?.value) {
        this.#updateSummary();
        return;
      }
      if (!picker.variants.length) return;

      this.#initMainDefaults(picker);
      this.#bindMainEvents(picker);
      this.#updateMainDisplay(picker);
    };

    return picker;
  }

  #initMainDefaults(picker) {
    if (picker.colorSwatches.length > 0) {
      const colorInput = picker.root.querySelector(`[data-option-input="${picker.colorPosition}"]`);
      const selectedValue = colorInput?.value;
      const activeSwatch =
        Array.from(picker.colorSwatches).find((swatch) => swatch.dataset.optionValue === selectedValue) ||
        picker.root.querySelector(".button-color-swatch.active") ||
        picker.colorSwatches[0];

      picker.colorSwatches.forEach((swatch) => this.#removeActive(swatch));
      this.#setActive(activeSwatch, true);
      picker.selectedOptions[picker.colorPosition] = activeSwatch.dataset.optionValue;
      this.#syncColorRadio(picker, activeSwatch);
      if (colorInput) colorInput.value = activeSwatch.dataset.optionValue;
    }

    if (picker.sizeUnknownBtn) {
      const unknownSize = this.#getUnknownSizeValue(picker);
      picker.selectedOptions[picker.sizePosition] = unknownSize;
      const sizeInput = picker.root.querySelector(`[data-option-input="${picker.sizePosition}"]`);
      if (sizeInput) sizeInput.value = unknownSize;
    }

    this.#updateSizeAvailability(picker);
    this.#findMainVariant(picker);
  }

  #bindMainEvents(picker) {
    const { signal } = this.#controller;

    picker.colorSwatches.forEach((swatch) => {
      swatch.addEventListener(
        "click",
        (e) => {
          const target = e.currentTarget;
          const color = target.dataset.optionValue;
          const position = target.dataset.optionPosition;

          picker.colorSwatches.forEach((s) => this.#removeActive(s));
          this.#setActive(target, true);
          this.#syncColorRadio(picker, target);
          picker.selectedOptions[position] = color;

          const input = picker.root.querySelector(`[data-option-input="${position}"]`);
          if (input) input.value = color;

          this.#updateSizeAvailability(picker);
          this.#findMainVariant(picker);
          this.#updateMainDisplay(picker);
        },
        { signal },
      );
    });

    picker.sizeUnknownBtn?.addEventListener("click", () => this.#handleSizeUnknown(picker), { signal });

    picker.sizeKnowBtn?.addEventListener(
      "click",
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.#toggleSizeOptions(picker);
      },
      { signal },
    );

    picker.sizeKnowOverlayBtn?.addEventListener(
      "click",
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.#toggleSizeOptions(picker);
      },
      { signal },
    );

    picker.sizeOptionBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => this.#handleSizeSelection(picker, e), { signal });
    });
  }

  #handleSizeUnknown(picker) {
    this.#setActive(picker.sizeUnknownBtn, true);
    this.#removeActive(picker.sizeKnowBtn);
    picker.sizeKnowBtn?.classList.remove("expanded");
    picker.sizeKnowOverlayBtn?.classList.remove("expanded", "active");
    this.#closeSizeOptions(picker);
    picker.sizeOptionBtns.forEach((btn) => this.#removeActive(btn));

    const unknownSize = this.#getUnknownSizeValue(picker);
    picker.selectedOptions[picker.sizePosition] = unknownSize;
    const input = picker.root.querySelector(`[data-option-input="${picker.sizePosition}"]`);
    if (input) input.value = unknownSize;
    if (picker.sizeBtnText) picker.sizeBtnText.textContent = picker.sizeBtnText.dataset.placeholder;
    if (picker.sizeBtnTextOverlay) {
      picker.sizeBtnTextOverlay.textContent = picker.sizeBtnTextOverlay.dataset.placeholder;
    }

    this.#findMainVariant(picker);
    this.#updateMainDisplay(picker);
  }

  #toggleSizeOptions(picker) {
    const isExpanded =
      picker.sizeDropdownOverlay &&
      picker.sizeDropdownOverlay.style.display !== "none" &&
      picker.sizeDropdownOverlay.style.display !== "";

    if (isExpanded) {
      this.#removeActive(picker.sizeKnowBtn);
      picker.sizeKnowOverlayBtn?.classList.remove("expanded", "active");
      this.#closeSizeOptions(picker);

      const activeSizeBtn = Array.from(picker.sizeOptionBtns).find((btn) => btn.classList.contains("active"));
      if (activeSizeBtn) {
        const size = activeSizeBtn.dataset.optionValue;
        if (picker.sizeBtnText) {
          const clickText = picker.sizeBtnText.dataset.clickText || "Size: {size}";
          picker.sizeBtnText.textContent = clickText.replace("{size}", size);
        }
        this.#setActive(picker.sizeKnowBtn, true);
      } else {
        this.#handleSizeUnknown(picker);
      }
      return;
    }

    this.#setActive(picker.sizeKnowBtn, true);
    picker.sizeKnowBtn?.classList.add("expanded");
    picker.sizeKnowOverlayBtn?.classList.add("expanded", "active");
    this.#removeActive(picker.sizeUnknownBtn);

    const openPlaceholder =
      picker.sizeBtnTextOverlay?.dataset.placeholder || picker.sizeBtnText?.dataset.placeholder || "Select Your Size";
    if (picker.sizeBtnText) picker.sizeBtnText.textContent = openPlaceholder;
    if (picker.sizeBtnTextOverlay) picker.sizeBtnTextOverlay.textContent = openPlaceholder;

    if (picker.sizeOptions && picker.sizeDropdownOverlay) {
      picker.sizeDropdown?.classList.add("is-open");
      picker.sizeKnowBtn?.setAttribute("aria-expanded", "true");
      picker.sizeKnowOverlayBtn?.setAttribute("aria-expanded", "true");
      picker.sizeOptions.classList.add("show");
      picker.sizeDropdownOverlay.style.display = "block";
      picker.sizeOptions.style.display = "block";
    }
  }

  #handleSizeSelection(picker, e) {
    const btn = e.currentTarget;
    if (btn.disabled) return;

    const size = btn.dataset.optionValue;
    const position = btn.dataset.optionPosition;

    picker.sizeOptionBtns.forEach((b) => this.#removeActive(b));
    this.#setActive(btn, true);
    this.#setActive(picker.sizeKnowBtn, true);
    picker.sizeKnowOverlayBtn?.classList.add("active");
    this.#removeActive(picker.sizeUnknownBtn);

    picker.selectedOptions[position] = size;
    const input = picker.root.querySelector(`[data-option-input="${position}"]`);
    if (input) input.value = size;

    if (picker.sizeBtnText) {
      const clickText = picker.sizeBtnText.dataset.clickText || "Size: {size}";
      picker.sizeBtnText.textContent = clickText.replace("{size}", size);
    }
    if (picker.sizeBtnTextOverlay) {
      const clickText = picker.sizeBtnTextOverlay.dataset.clickText || "Size: {size}";
      picker.sizeBtnTextOverlay.textContent = clickText.replace("{size}", size);
    }

    this.#closeSizeOptions(picker);
    this.#findMainVariant(picker);
    this.#updateMainDisplay(picker);
  }

  #closeSizeOptions(picker) {
    if (picker.sizeOptions) {
      picker.sizeOptions.style.display = "none";
      picker.sizeOptions.classList.remove("show");
    }
    if (picker.sizeDropdownOverlay) {
      picker.sizeDropdownOverlay.style.display = "none";
      picker.sizeDropdownOverlay.style.width = "";
      picker.sizeDropdownOverlay.style.right = "";
    }
    picker.sizeDropdown?.classList.remove("is-open");
    picker.sizeKnowBtn?.classList.remove("expanded");
    picker.sizeKnowBtn?.setAttribute("aria-expanded", "false");
    picker.sizeKnowOverlayBtn?.classList.remove("expanded", "active");
    picker.sizeKnowOverlayBtn?.setAttribute("aria-expanded", "false");
  }

  #updateSizeAvailability(picker) {
    const selectedColor = picker.selectedOptions[picker.colorPosition];
    if (!selectedColor) return;

    picker.sizeOptionBtns.forEach((btn) => {
      const size = btn.dataset.optionValue;
      const variant = picker.variants.find(
        (v) =>
          this.#getVariantOptionValue(v, picker.colorPosition) === selectedColor &&
          this.#getVariantOptionValue(v, picker.sizePosition) === size,
      );

      if (variant?.available) {
        btn.disabled = false;
        btn.classList.remove("disabled");
      } else {
        btn.disabled = true;
        btn.classList.add("disabled");
        if (btn.classList.contains("active")) {
          this.#removeActive(btn);
          this.#handleSizeUnknown(picker);
        }
      }
    });
  }

  #findMainVariant(picker) {
    const option1 = picker.selectedOptions[picker.colorPosition];
    const option2 = picker.selectedOptions[picker.sizePosition];

    picker.currentVariant =
      picker.variants.find((variant) => {
        const match1 = !option1 || this.#getVariantOptionValue(variant, picker.colorPosition) === option1;
        const match2 = !option2 || this.#getVariantOptionValue(variant, picker.sizePosition) === option2;
        return match1 && match2;
      }) || null;

    if (picker.currentVariant && picker.variantIdInput) {
      picker.variantIdInput.value = picker.currentVariant.id;
    }

    const legacyInput = picker.root.querySelector("[data-bts-main-variant-id]");
    if (picker.currentVariant && legacyInput) {
      legacyInput.value = picker.currentVariant.id;
    }

    return picker.currentVariant;
  }

  #updateMainDisplay(picker) {
    const variant = picker.currentVariant;
    if (variant) {
      this.#updateCardImage(picker.media, variant, picker);
      if (this.mainPriceEl && typeof this.getFormattedMoney === "function") {
        this.mainPriceEl.textContent = this.getFormattedMoney(variant.price, this.currencyCodeEnabled);
      }
    }
    this.#updateSummary();
    this.#updateAtcState();
  }

  /* ----------------------------- Addon picker ---------------------------- */

  #createAddonPicker(card) {
    if (!card) return null;

    const picker = {
      root: card,
      media: card.querySelector("[data-bts-addon-media] img"),
      variantIdInput: card.querySelector("[data-bts-addon-variant-id]"),
      colorButtons: card.querySelectorAll("[data-bts-option] .bts-add-on__color"),
      colorWrap: card.querySelector("[data-bts-option]"),
      variants: this.#loadVariantData(card, "[data-bts-addon-variants]"),
      currentVariant: null,
      selectedColor: null,
      colorPosition: card.querySelector("[data-bts-option]")?.dataset.optionPosition || "1",
    };

    picker.init = () => {
      if (picker.colorButtons.length > 0) {
        const active =
          Array.from(picker.colorButtons).find((btn) => btn.classList.contains("is-active")) || picker.colorButtons[0];
        this.#selectAddonColor(picker, active);
        this.#bindAddonEvents(picker);
      } else if (picker.variantIdInput?.value) {
        picker.currentVariant =
          picker.variants.find((v) => String(v.id) === String(picker.variantIdInput.value)) ||
          picker.variants[0] ||
          null;
      }
      this.#updateAddonDisplay(picker);
    };

    return picker;
  }

  #bindAddonEvents(picker) {
    const { signal } = this.#controller;
    picker.colorButtons.forEach((btn) => {
      btn.addEventListener(
        "click",
        () => {
          this.#selectAddonColor(picker, btn);
          this.#updateAddonDisplay(picker);
        },
        { signal },
      );
    });
  }

  #selectAddonColor(picker, btn) {
    picker.colorButtons.forEach((item) => {
      const active = item === btn;
      item.classList.toggle("is-active", active);
      item.setAttribute("aria-pressed", active ? "true" : "false");
    });

    picker.selectedColor = btn.dataset.optionValue;
    const input = picker.colorWrap?.querySelector("[data-option-input]");
    if (input) input.value = picker.selectedColor;

    picker.currentVariant =
      picker.variants.find((v) => this.#getVariantOptionValue(v, picker.colorPosition) === picker.selectedColor) ||
      picker.variants.find((v) => [v.option1, v.option2, v.option3].includes(picker.selectedColor)) ||
      null;

    if (picker.currentVariant && picker.variantIdInput) {
      picker.variantIdInput.value = picker.currentVariant.id;
    }
  }

  #updateAddonDisplay(picker) {
    if (picker.currentVariant) {
      this.#updateCardImage(picker.media, picker.currentVariant, picker);
      const compare = picker.currentVariant.compare_at_price || picker.currentVariant.price;
      if (this.addonCompareEl && typeof this.getFormattedMoney === "function") {
        if (compare > this.addonPromoCents) {
          this.addonCompareEl.textContent = this.getFormattedMoney(compare, this.currencyCodeEnabled);
          this.addonCompareEl.style.display = "";
        } else {
          this.addonCompareEl.style.display = "none";
        }
      }
    }
    this.#syncAddonBridge();
    this.#updateSummary();
    this.#updateAtcState();
  }

  /* ------------------------------ Summary / ATC ------------------------------ */

  #bindGlobalEvents() {
    const { signal } = this.#controller;

    this.addonToggle?.addEventListener(
      "click",
      () => {
        this.#addonChecked = !this.#addonChecked;
        this.addonToggle.setAttribute("aria-pressed", this.#addonChecked ? "true" : "false");
        this.addonCard?.classList.toggle("is-unchecked", !this.#addonChecked);
        this.#syncAddonBridge();
        this.#updateSummary();
        this.#updateAtcState();
      },
      { signal },
    );

    // 提交前同步主变体 / addon bridge，交给 product-form → BuyOneGetOneFree
    this.form?.addEventListener("submit", () => this.#syncFormForSubmit(), { capture: true, signal });
  }

  /** 同步 checkbox，供 BuyOneGetOneFree 按当前表单扫描 .addon-card__checkbox */
  #syncAddonBridge() {
    if (!this.addonBridge) return;

    const addonVariant = this.#getAddonVariant();
    const checked = Boolean(this.#addonChecked && addonVariant?.id);
    this.addonBridge.checked = checked;
    this.addonBridge.disabled = !checked;
    if (addonVariant?.id) {
      this.addonBridge.value = String(addonVariant.id);
    }

    if (checked && this.discountCode) {
      this.addonBridge.dataset.discount = this.discountCode;
    } else {
      delete this.addonBridge.dataset.discount;
    }

    if (this.discountInput) {
      this.discountInput.disabled = !checked || !this.discountCode;
    }
  }

  #syncFormForSubmit() {
    const mainVariant = this.#getMainVariant();
    if (mainVariant?.id && this.form) {
      this.form.querySelectorAll('input[name="id"]').forEach((input) => {
        input.value = String(mainVariant.id);
      });
    } else if (mainVariant?.id && this.main?.variantIdInput) {
      this.main.variantIdInput.value = String(mainVariant.id);
    }
    this.#syncAddonBridge();
  }

  #getMainVariant() {
    if (this.main?.currentVariant) return this.main.currentVariant;
    const fromList = this.#findVariantById(this.main?.variants, this.main?.variantIdInput?.value);
    if (fromList) return fromList;

    const id = this.main?.variantIdInput?.value || this.mainCard?.querySelector("[data-bts-main-variant-id]")?.value;
    if (!id) return null;

    return {
      id: Number(id),
      available: true,
      price: Number(this.mainCard?.dataset.initialPrice || 0),
    };
  }

  #getAddonVariant() {
    if (!this.#addonChecked || !this.addonCard) return null;
    if (this.addon?.currentVariant) return this.addon.currentVariant;
    const fromList = this.#findVariantById(this.addon?.variants, this.addon?.variantIdInput?.value);
    if (fromList) return fromList;

    const id = this.addon?.variantIdInput?.value;
    if (!id) return null;

    return {
      id: Number(id),
      available: true,
      price: Number(this.addonCard?.dataset.initialPrice || 0),
      compare_at_price: Number(this.addonCard?.dataset.initialCompare || 0),
    };
  }

  #updateSummary() {
    const mainVariant = this.#getMainVariant();
    const addonVariant = this.#getAddonVariant();
    const format = (cents) =>
      typeof this.getFormattedMoney === "function"
        ? this.getFormattedMoney(cents, this.currencyCodeEnabled)
        : String(cents);

    const mainPrice = mainVariant?.price || 0;
    if (this.sumMainEl) this.sumMainEl.textContent = format(mainPrice);
    if (this.mainPriceEl && mainVariant) this.mainPriceEl.textContent = format(mainPrice);

    const showAddon = Boolean(addonVariant) && this.#addonChecked;
    this.sumAddonRow?.classList.toggle("is-hidden", !showAddon);
    this.sumSaveRow?.classList.toggle("is-hidden", !showAddon);
    if (this.sumAddonRow) this.sumAddonRow.style.display = showAddon ? "" : "none";
    if (this.sumSaveRow) this.sumSaveRow.style.display = showAddon ? "" : "none";

    let total = mainPrice;
    if (showAddon) {
      const compare = addonVariant.compare_at_price || addonVariant.price || 0;
      const promo = this.addonPromoCents;
      total += promo;

      if (this.sumAddonPriceEl) this.sumAddonPriceEl.textContent = format(promo);
      if (this.addonPriceEl) this.addonPriceEl.textContent = format(promo);

      if (this.sumAddonCompareEl) {
        if (compare > promo) {
          this.sumAddonCompareEl.textContent = format(compare);
          this.sumAddonCompareEl.style.display = "";
        } else {
          this.sumAddonCompareEl.style.display = "none";
        }
      }

      const save = Math.max(0, compare - promo);
      if (this.sumSaveEl) this.sumSaveEl.textContent = format(save);
    }

    if (this.sumTotalEl) this.sumTotalEl.textContent = format(total);
  }

  #updateAtcState() {
    if (!this.atcBtn) return;
    const mainVariant = this.#getMainVariant();
    const mainOk = Boolean(mainVariant?.id) && mainVariant.available !== false;
    const addonVariant = this.#getAddonVariant();
    const addonOk =
      !this.#addonChecked || !this.addonCard || (Boolean(addonVariant?.id) && addonVariant.available !== false);
    const available = mainOk && addonOk;

    this.atcBtn.disabled = !available;
    this.atcBtn.classList.toggle("disabled", !available);
    if (this.atcTextEl) {
      this.atcTextEl.textContent = available ? this.ctaDefaultText : this.soldOutText;
    }
  }

  /* -------------------------------- Helpers -------------------------------- */

  #loadVariantData(root, selector = "[data-variant-data]") {
    const dataScript = root?.querySelector(selector);
    if (!dataScript) return [];
    try {
      const data = JSON.parse(dataScript.textContent);
      return data.variants || data || [];
    } catch (e) {
      console.error("[bts-add-on-manager] variant data parse error", e);
      return [];
    }
  }

  #findVariantById(variants, id) {
    if (!id || !variants?.length) return null;
    return variants.find((v) => String(v.id) === String(id)) || null;
  }

  #getVariantOptionValue(variant, position) {
    if (!variant || !position) return null;
    return variant[`option${position}`] ?? null;
  }

  #isUnknownSizeValue(value) {
    if (!value) return false;
    const normalized = String(value).trim().toLowerCase();
    return (
      normalized.includes("don't know") ||
      normalized.includes("dont know") ||
      normalized.includes("不知道") ||
      normalized.includes("sizing kit")
    );
  }

  #getUnknownSizeValue(picker) {
    if (picker.unknownSizeValue) return picker.unknownSizeValue;
    for (const variant of picker.variants) {
      for (const position of [picker.colorPosition, picker.sizePosition, "3"]) {
        const value = this.#getVariantOptionValue(variant, position);
        if (this.#isUnknownSizeValue(value)) return value;
      }
    }
    return "I don't know my size";
  }

  #syncColorRadio(picker, activeSwatch) {
    picker.colorSwatches.forEach((swatch) => {
      const radio = swatch.querySelector('input[type="radio"]');
      if (radio) radio.checked = swatch === activeSwatch;
    });
  }

  #setActive(el, active) {
    if (!el) return;
    el.classList.toggle("active", active);
    el.classList.toggle("is-active", active);
  }

  #removeActive(el) {
    if (!el) return;
    el.classList.remove("active", "is-active");
  }

  #updateCardImage(img, variant, picker) {
    if (!img || !variant) return;
    let imageUrl = variant.featured_image;
    if (!imageUrl && picker?.colorPosition) {
      const color = this.#getVariantOptionValue(variant, picker.colorPosition);
      const fallback = picker.variants?.find(
        (v) => this.#getVariantOptionValue(v, picker.colorPosition) === color && v.featured_image,
      );
      imageUrl = fallback?.featured_image || null;
    }
    if (imageUrl) {
      img.src = imageUrl;
      img.srcset = imageUrl;
    }
  }
}

if (!customElements.get("bts-add-on-manager")) {
  customElements.define("bts-add-on-manager", BtsAddOnManager);
}
